import {useEffect, useState} from 'react';
import {Paper, Box} from '@mui/material';
import {GridColDef, GridColumnGroupingModel} from '@mui/x-data-grid-premium';
import {StripedDataGrid} from '@components/datagrid/DataGridComponentStyles';

const RCMDashboardTable = ({data, valueFormatter, ...props}: any) => {
  const {rows, colors, columnsList} = data;
  const [updatedRows, setUpdatedRows] = useState([]);
  const [pinnedRows, setPinnedRows] = useState([]);

  // Currency Formatter
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  const mainColumn = Object.keys(rows[0])?.[0];
  // Add custom rows at the bottom
  useEffect(() => {
    let tempUpdatedRows: any = [...rows];

    const additionalRows = ['Grand Total', 'Percentage'];

    tempUpdatedRows = tempUpdatedRows.filter(
      (row: any) =>
        !additionalRows.includes(row.category) ||
        props?.addOnRows?.includes(row.category)
    );

    setUpdatedRows(tempUpdatedRows);
    setPinnedRows(
      tempUpdatedRows.filter(
        (row: any) =>
          row[mainColumn] === 'Grand Total' || row[mainColumn] === 'Percentage'
      )
    );
  }, [rows]);

  const checkMainColumn = (col: any) => {
    const mainColumnList = [
      'category',
      'financialclass',
      'grandtotal',
      'percentage'
    ];

    if (mainColumnList.includes(col.trim().toLowerCase())) {
      return true;
    }
    return false;
  };

  // Generate Columns
  const COLUMNS: GridColDef[] = Object.keys(rows[0]).map(
    (item: string, index: number) => ({
      field: item,
      headerName: data?.splitColumnForHeaderWith
        ? item.split(data.splitColumnForHeaderWith)[0]?.toUpperCase()
        : item.toUpperCase(),
      width: 150,
      flex: 1,
      filterable: true,
      sortable: true,
      headerAlign: checkMainColumn(item) || index == 0 ? 'left' : 'center', // Center align header
      align: checkMainColumn(item) || index == 0 ? 'left' : 'center', // Center align cell content
      renderCell: (params: any) => {
        if (checkMainColumn(item)) {
          //For Columns
          if (['Grand Total', 'Percentage'].includes(item))
            return params.row[item];
          //For Rows
          if (['Grand Total', 'Percentage'].includes(params.row[item])) {
            return params.row[item];
          }
          return (
            <div style={{display: 'flex', alignItems: 'center'}}>
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  backgroundColor: colors[params.row[item]] ?? 'white', // Use dynamic color
                  marginRight: '8px'
                }}></div>
              {params.row[item]}
            </div>
          );
        }

        return params.row.value;
      },
      valueFormatter: ({value}: any) => {
        if (!valueFormatter) return value;
        if (value == null || value == undefined) {
          return '';
        }
        if (
          valueFormatter == 'currency' &&
          item !== 'id' &&
          typeof value !== 'string'
        ) {
          return currencyFormatter.format(value);
        }
        return value;
      }
    })
  );

  // const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
  //   page: 0,
  //   pageSize: 10
  // });

  const groupedColumnList = data?.groupedColumn
    ? columnsList?.slice(1).map((col_item: any) => ({
        groupId: col_item,
        children: Object.keys(rows[0])
          .filter(row_item => row_item.includes(col_item))
          .map(row_item => ({field: row_item}))
      }))
    : [];
  const columnGroupingModel: GridColumnGroupingModel = data?.groupedColumn
    ? [
        {
          groupId: 'Internal',
          headerName: '',
          description: '',
          children: [{field: columnsList[0]}]
        },
        ...groupedColumnList,
        {
          groupId: 'Total',
          description: '',
          children: props?.addOnColumns?.length
            ? props?.addOnColumns.map((item: any) => {
                return {field: item};
              })
            : []
        }
      ]
    : [];

  const normalRows = updatedRows.filter(
    row => row[mainColumn] !== 'Grand Total' && row[mainColumn] !== 'Percentage'
  );

  return (
    <Box component={Paper}>
      <StripedDataGrid
        rows={normalRows}
        experimentalFeatures={{columnGrouping: true}}
        columnGroupingModel={columnGroupingModel}
        columns={COLUMNS}
        getRowId={row => row.rowId}
        onFilterModelChange={(model: any) => {
          if (model.items?.find((item: any) => item.value?.length)) {
            setPinnedRows([]);
          } else {
            setPinnedRows(
              updatedRows.filter(
                (row: any) =>
                  row[mainColumn] === 'Grand Total' ||
                  row[mainColumn] === 'Percentage'
              )
            );
          }
        }}
        pinnedRows={{bottom: pinnedRows}}
        hideFooter={true}
        density="compact"
        // pagination
        // pageSizeOptions={[5, 10, 20, 50]}
        // paginationModel={paginationModel}
        // onPaginationModelChange={setPaginationModel}
        columnVisibilityModel={{
          rowId: false // this will be hidden
        }}
        getRowClassName={params =>
          params.row[mainColumn] === 'Grand Total'
            ? 'grand-total-row'
            : params.row[mainColumn] === 'Percentage'
              ? 'percentage-row'
              : ''
        }
        sx={{
          '& .grand-total-row': {
            fontWeight: 'bold'
          },
          '& .percentage-row': {
            fontWeight: 'bold'
          }
        }}
      />
    </Box>
  );
};

export default RCMDashboardTable;
